/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  TemplateRef,
  computed,
  contentChild,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  HmfCheckboxComponent,
  HmfRadioComponent,
  HmfSelectorComponent,
} from '@dsg-tech/homefield-angular';

import { FacetVOValue } from '@plp/data-access/interfaces';
import { FilterOptionType } from './filter-option.enum';
import { GTGT_FILTER_NAME } from '@plp/data-access/constants';
import { NgTemplateOutlet } from '@angular/common';
import { USWholeNumberPipe } from '@shared/utils/pipes';

@Component({
  selector: 'plp-components-filter-option',
  templateUrl: './filter-option.component.html',
  styleUrls: ['./filter-option.component.scss'],
  imports: [
    HmfCheckboxComponent,
    HmfRadioComponent,
    HmfSelectorComponent,
    NgTemplateOutlet,
    USWholeNumberPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterOptionComponent {
  readonly attributeIdentifier = input.required<string>();
  readonly attributeName = input.required<string>();
  readonly filterOption = input.required<FacetVOValue>();
  readonly ariaLabel = input.required<string>();
  readonly displayValue = input.required<string>();

  readonly isMobile = input(false);
  readonly disabled = input(false);
  readonly selectedOptions = input<string[]>([]);
  readonly filterOptionType = input<FilterOptionType>(FilterOptionType.Checkbox);
  readonly labelAsHmfInputContent = input(false); // whether the label should be passed into the hmf-checkbox or hmf-radio component instead of rendered outside it

  readonly projectedLabelTemplate = contentChild<TemplateRef<unknown>>('projectedLabel');

  readonly radioElement = viewChild(HmfRadioComponent);
  readonly checkboxElement = viewChild(HmfCheckboxComponent);

  FilterOptionType = FilterOptionType;
  GTGT_FILTER_NAME = GTGT_FILTER_NAME;
  MULTICOLOR_IMAGE =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAQCAIAAACZeshMAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAHxJREFUeNpifCuvwoAEuMp/IXNNNjYgc1/aLkbmMjFQAIaoZsZL2XnI/KO3UKQN8/cic536viFzWZ54eyLzH+y/hMxNVbmJzP22788wCDAWhe8cyPzHpnLI3PtvUEJETX4fimb5b+zIfM0FEcjcXRvfI3NV5U+O9OQJEGAAwBofdtN6GyIAAAAASUVORK5CYII=';

  readonly setSelectedFilter = output<string>();
  readonly setSelectedFilterWithSpace = output<{ event: Event; value: string }>();

  readonly checked: Signal<boolean> = computed(() =>
    this.selectedOptions().includes(this.filterOption().value),
  );

  handleSpace(event: Event): void {
    // Focus must fire first, otherwise we start the `afterFilterChange()` re-focusing logic before the checkbox is focused
    this.focusInput();
    this.setSelectedFilterWithSpace.emit({ event, value: this.filterOption().value });
  }

  handleClick(): void {
    // Focus must fire first, otherwise we start the `afterFilterChange()` re-focusing logic before the checkbox is focused
    this.focusInput();
    this.setSelectedFilter.emit(this.filterOption().value);
  }

  /**
   * Manually focus the input element of the checkbox or radio component.
   * This is necessary because we are not using a real label element, so clicks on the label won't
   * trigger the input element to be focused.
   *
   * Remove in favor of passing in labels as projected content once https://dcsgcloud.atlassian.net/browse/HMF-1692 is done.
   *
   * @private
   */
  private focusInput(): void {
    const inputElementRef =
      this.checkboxElement()?.checkboxInput ?? this.radioElement()?.inputElement;
    if (inputElementRef) {
      inputElementRef.nativeElement.focus();
    }
  }
}
